import Fonts from "baseStyles/enums/fonts";
import { Colors } from "baseStyles/enums/colors";
import React, { useEffect, useMemo, useRef, useState } from "react";
import Box from "schoolUiComponents/basic/Box";
import FlexBox from "schoolUiComponents/basic/Box/FlexBox";
import Button from "schoolUiComponents/basic/Button";
import Input from "schoolUiComponents/basic/Input";
import { InputTypes } from "schoolUiComponents/basic/Input/types";
import Text from "schoolUiComponents/basic/Text";
import InboxImg from './../assets/inbox.svg';
import ActivitiesImg from './../assets/activities.svg';
import ReportsImg from './../assets/reports.svg';
import SignupStyles from "./style";
import { APIS } from "../api";
import { interactedPtlParentSignupScreenEvent, viewedErrorOnPtlParentSignupScreenEvent, viewedPtlParentSignupScreenEvent } from "../events";
import Checkbox, { CheckboxValue } from "schoolUiComponents/basic/Checkbox";
import PositionBox from "schoolUiComponents/basic/Box/PositionBox";
import { history } from "../history";
import { REFERRER_PAGE_MAP, SUB_SOURCE_MAP } from "utility/referrer";
import { DateDiffInDays } from "../../teacherDashboard/teacher/config/common";

const FOOTER_ITEMS = [
    {
        image: <InboxImg />,
        text: "Receive assignment alerts directly in your inbox",
    },
    {
        image: <ActivitiesImg />,
        text: "Get access to teacher assigned activities",
    },
    {
        image: <ReportsImg />,
        text: "Stay on top of your child’s progress with comprehensive reports",
    }
];

interface IReferralData {
    referrerPage: string;
    invitationSource: string;
    subSource?: string;
};

export const Signup = () => {
    const queryParams = new URLSearchParams(window.location.search);
    //@ts-ignore
    const email = queryParams.get("masked_email") || window.parentEmail;
    const [parentEmail, setParentEmail] = useState<string | null>(email);
    const [tnc, setTnc] = useState(CheckboxValue.UNCHECKED);
    const [emailPref, setEmailPref] = useState(CheckboxValue.UNCHECKED);
    const [error, setError] = useState(null);
    const [isTyping, setIsTyping] = useState(false);
    const [inputMethod, setInputMethod] = useState('');

    const urlParams = new URLSearchParams(window.location.search);

    // Compute referral data immediately on component load, not in useEffect
    const referrerPage = REFERRER_PAGE_MAP[urlParams.get("referrer_code")] || "default";
    const invitationSource = urlParams.get("referral_type") || "";
    const tr_sub_source = urlParams.get("tr_sub_source");
    const subSource = SUB_SOURCE_MAP[tr_sub_source || ""] || localStorage.getItem("subSource") || undefined;
    const referralData: IReferralData = { referrerPage, invitationSource, subSource };
    const [, setReferralData] = useState<IReferralData>(referralData);

    useEffect(() => {
        if (subSource) {
            localStorage.setItem("subSource", subSource);
        }
    }, []);

    const styles = SignupStyles();
    const emailFieldDirty = useRef(false);
    // @ts-ignore
    const userConsentRequired = window.userConsentRequired;

    const isEmailValid = () => {
        if (!emailFieldDirty.current) {
            return true;
        }
        const pEmail = parentEmail?.trim();
        const emailRegex = /^[a-zA-Z0-9,!#\$%&'\*\+\/=\?\^_`\{\|}~-]+(\.[a-zA-Z0-9,!#\$%&'\*\+\/=\?\^_`\{\|}~-]+)*@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*\.([a-zA-Z]{2,})$/;
        if (!pEmail?.length) {
            setError("Enter parent's email");
            return false;
        }
        if (!emailRegex?.test(pEmail)) {
            setError("Email is invalid");
            return false;
        }
        setError(null);
        return true;
    }

    useEffect(() => {
        if(error?.length) {
            isEmailValid();
        }
    }, [parentEmail, emailFieldDirty])

    useEffect(() => {
        // @ts-ignore
        if(window.parentEmail && window.analyticsId){
            trackEventsForGuests("LP_HomeAccess_rendered", {
                // @ts-ignore
                "Platform": window.deviceDetails?.platform,
                // @ts-ignore
                "Device opened": window.deviceDetails?.device_os,
                // @ts-ignore
                "Email": window.parentEmail,
                "custom_user_type": "guest_ptl_parent",
                // @ts-ignore
                "analytics_id": window.analyticsId
            }, { send_datadump: true, send_mixpanel: true, send_clevertap: false });
        }
    }, [])

    const handleSignup = (event: any) => {
        event?.stopPropagation();
        event?.preventDefault();
        interactedPtlParentSignupScreenEvent({ clickedOn: "Continue", ...referralData })
        if (!isEmailValid()) {
            viewedErrorOnPtlParentSignupScreenEvent({ errorMessage: "Email is invalid", ...referralData })
            return;
        }
        if(userConsentRequired && (tnc != CheckboxValue.CHECKED)) {
            return;
        }
        const queryParams = new URLSearchParams(window.location.search);
        const referral_type = queryParams.get("referral_type");
        const referrer_code = queryParams.get("referrer_code");
        const student_id = queryParams.get("student_id");
        const trd_id = queryParams.get("trd_id");
        const tr_sub_source = queryParams.get("tr_sub_source");
        const pEmail = parentEmail?.trim();
        const view_due_date = queryParams.get("view_due_date");
        const task_count = queryParams.get("task_count");
        const nearest_due_date = queryParams.get("nearest_due_date");
        APIS.validatePtlSetup(pEmail, student_id).then((res) => {
            if (res.data.success) {
                APIS.createUser({ parentEmail: pEmail, student_id, referral_type, referrer_code, tr_sub_source, trd_id, new_ptl_flow: true, email_preference: emailPref === CheckboxValue.CHECKED ? 1 : 0, tnc_privacy: tnc === CheckboxValue.CHECKED ? 1 : 0 }).then((res1) => {
                    const searchParams = new URLSearchParams(window.location.search);
                    searchParams.append("new_ptl_flow", "true");
                    // [AI GENERATED CODE] New parent created — tag session with parent_signup source
                    if (typeof (window as any).MUS !== 'undefined') (window as any).MUS.setPendingSource('parent_signup');
                    window.location.href = `/parent-dashboard#/?${searchParams}`;
                }).catch((error) => {
                    console.error(error);
                    setError(error?.response?.data?.errors?.[0] || 'Something went wrong!')
                })
            } else {
                if (res.data.parent_exist) {
                    if(res.data.unconfirmed_user){
                        const searchParams = new URLSearchParams(window.location.search);
                        searchParams.append("new_ptl_flow", "true");
                        // [AI GENERATED CODE] Unconfirmed existing parent completing signup — tag with parent_signup
                        if (typeof (window as any).MUS !== 'undefined') (window as any).MUS.setPendingSource('parent_signup');
                        window.location.href = `/parent-dashboard#/?${searchParams}`;
                    }else{
                        const token = btoa(JSON.stringify({ student_id, masked_email: pEmail, referral_type, referrer_code, tr_sub_source, trd_id, view_due_date, task_count, nearest_due_date }));
                        window.location.href = `${res.data.redirect_url || "/ptl-login"}?token=${token}`;
                    }
                } else {
                    setError('Something went wrong!')
                }
            }
        }).catch((error) => {
            setError(error?.response?.data?.error_message || 'Something went wrong!')
            viewedErrorOnPtlParentSignupScreenEvent({ errorMessage: error?.response?.data?.error_property || 'Something went wrong!', ...referralData })
        })
    }

    useEffect(() => {
        const assignmentDueInDays = urlParams.get("nearest_due_date") && (urlParams.get("nearest_due_date") !== 'null') ? String(DateDiffInDays(new Date(), new Date(urlParams.get("nearest_due_date")))) : 'na';
        const viewedDueDateOnInvitation = urlParams.get("view_due_date") === "true" ? true : false;
        const totalAssignmentCount = urlParams.get("task_count") && (urlParams.get("task_count") !== "null") ? Number(urlParams.get("task_count") || 0) : 0;
        viewedPtlParentSignupScreenEvent({ referrerPage, invitationSource, assignmentDueInDays, viewedDueDateOnInvitation, totalAssignmentCount, subSource });
    },[])

    const sendEventOnPaste = () => {
        setInputMethod('pasted');
        interactedPtlParentSignupScreenEvent({ clickedOn: "Email", interactionDetails: "Copy pasted", ...referralData })
    }

    const onChangeEmailEvent = () => {
        if(!isTyping && (inputMethod !== 'pasted')){
            interactedPtlParentSignupScreenEvent({ clickedOn: "Email", interactionDetails: "Started typing", ...referralData })
            setIsTyping(true)
        }
      }
    const backHandler = () => {
        history.goBack();
        interactedPtlParentSignupScreenEvent({ clickedOn: "Back button", ...referralData })
    }

    const referral_type = queryParams.get("referral_type");

    return (
        <FlexBox relative dataTestId="ptl-signup-screen" fullWidth backgroundColor={Colors.WHITE} minHeight={`${window.innerHeight - 61}px`} column>
            {(history.length > 2) && (referral_type === "copy_link")  && <PositionBox.absolute onClick={backHandler} left={34} top={36}><Text.H2 color={Colors.PURE_PURPLE} hoverColor={Colors.NAVY_BLUE}><i style={{fontSize: '18px' }} className="iconcust-iconBackBtn"></i></Text.H2></PositionBox.absolute>}
            <FlexBox flex={1} center fullWidth>
                <form onSubmit={handleSignup} autoComplete="on">
                    <FlexBox {...styles.mainBox} center column>
                        <FlexBox column gap={8}>
                            <Text.H3 color={Colors.CONTENT_TEXT_SECONDARY} fontWeight={Fonts.Weight.W400}>Welcome parent!</Text.H3>
                            <Text.H2 fullWidth lines={2} emphasis center>Set up SplashLearn home access</Text.H2>
                        </FlexBox>
                        <FlexBox gap={8} margin={"8px 0 0 0"} column fullWidth alignStart>
                            <Input onBlur={isEmailValid} color={Colors.DARK_GREY} hasError={error} value={parentEmail} onChange={(e) => {
                                emailFieldDirty.current = true;
                                setParentEmail(e.target?.value)
                                onChangeEmailEvent()
                            }}
                            onClick={() => interactedPtlParentSignupScreenEvent({ clickedOn: "Email input", ...referralData})}
                            onPaste={sendEventOnPaste}
                             width={styles.mainBox.width} customPlaceholder placeholder="Parent email" padding={"16px 24px"} autoFocus />
                            {!!error?.length && <Box padding={"0 24px"} fullWidth><Text.SMALL color={Colors.DARK_RED_2} lines={3} fullWidth><span dangerouslySetInnerHTML={{__html: error}}/></Text.SMALL></Box>}
                        </FlexBox>
                        {
                            userConsentRequired &&
                            <>
                                <FlexBox gap={12} alignStart fullWidth>
                                    <Box margin={"4px 0 0 8px"}><Checkbox checked={tnc} onClick={setTnc} /></Box>
                                    <FlexBox flex={1}>
                                        <Text color={Colors.CONTENT_TEXT_SECONDARY} lines={10} fullWidth>
                                            <Text color={Colors.DARK_RED_2}>*</Text> I confirm that I am at least 18 years of age and agree to SplashLearn’s <Text onClick={() => window.open("/terms-of-use", "_system")} color={Colors.CONTENT_TEXT_SECONDARY} cta>Terms & conditions</Text> and <Text onClick={() => window.open("/privacy", "_system")} color={Colors.CONTENT_TEXT_SECONDARY} cta>Privacy policy</Text>.
                                        </Text>
                                    </FlexBox>
                                </FlexBox>
                                <FlexBox gap={12} alignStart fullWidth>
                                    <Box margin={"4px 0 0 8px"}><Checkbox checked={emailPref} onClick={setEmailPref} /></Box>
                                    <FlexBox flex={1}>
                                        <Text color={Colors.CONTENT_TEXT_SECONDARY} lines={10} fullWidth>
                                            I want to receive newsletters, educational resources and promotional offers from SplashLearn. You can unsubscribe anytime in the future by using unsubscribe link in any of our email.
                                        </Text>
                                    </FlexBox>
                                </FlexBox>
                            </>
                        }
                        <Button disabled={userConsentRequired && (tnc != CheckboxValue.CHECKED)} primary onClick={handleSignup}><Text.H4 color={Colors.WHITE}>Continue</Text.H4></Button>
                    </FlexBox>
                </form>
            </FlexBox>
            <FlexBox {...styles.footerStyle} fullWidth center shadow>
                <FlexBox {...styles.footerContentStyle} gap={24} center>
                    {
                        FOOTER_ITEMS.map((item) => (
                            <FlexBox gap={16} column center>
                                <Box>{item.image}</Box>
                                <Text.CAPTION width={224} lines={2} center>{item.text}</Text.CAPTION>
                            </FlexBox>
                        ))
                    }
                </FlexBox>
            </FlexBox>
        </FlexBox>
    )
}